import type { DashboardData } from "@crewledger/shared/schemas";
import { cn } from "@/lib/utils";

interface CrewStatusRingProps {
  crewStatus: DashboardData["crewStatus"] | null;
  loading: boolean;
}

const SEGMENTS = [
  { key: "onSite", label: "On site", color: "stroke-green-approved" },
  { key: "missingEntries", label: "Missing entries", color: "stroke-amber-warning" },
  { key: "offSite", label: "Off site", color: "stroke-navy-blueprint" },
  { key: "unassigned", label: "Unassigned", color: "stroke-slate-300" },
] as const;

export function CrewStatusRing({ crewStatus, loading }: CrewStatusRingProps) {
  if (loading || !crewStatus) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border bg-card-white">
        <div className="h-32 w-32 animate-pulse rounded-full bg-navy-deep/8" />
      </div>
    );
  }

  const values = SEGMENTS.map((s) => ({
    ...s,
    value: crewStatus[s.key],
  }));

  const total = values.reduce((sum, s) => sum + s.value, 0) || 1;
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="rounded-xl border bg-card-white p-4 shadow-[var(--shadow-card)] sm:p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Crew status
      </h2>

      <div className="mt-4 flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:justify-around">
        <div className="relative">
          <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
            <circle
              cx="70"
              cy="70"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="14"
              className="text-bg-light"
            />
            {values.map((segment) => {
              const fraction = segment.value / total;
              const dash = fraction * circumference;
              const circle = (
                <circle
                  key={segment.key}
                  cx="70"
                  cy="70"
                  r={radius}
                  fill="none"
                  strokeWidth="14"
                  strokeLinecap="round"
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  strokeDashoffset={-offset}
                  className={cn(segment.color, "fill-none")}
                />
              );
              offset += dash;
              return circle;
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold tabular-nums text-navy-deep">{total}</span>
            <span className="text-xs text-muted-foreground">workers</span>
          </div>
        </div>

        <ul className="grid w-full max-w-xs grid-cols-2 gap-3 sm:max-w-none">
          {values.map((segment) => (
            <li key={segment.key} className="flex items-center gap-2 text-sm">
              <span className={cn("h-2.5 w-2.5 rounded-full", segment.color.replace("stroke-", "bg-"))} />
              <div>
                <p className="font-medium text-navy-deep">{segment.value}</p>
                <p className="text-xs text-muted-foreground">{segment.label}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
